<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import Button from '../../Button.svelte';
  import Input from '../../Input.svelte';
  import Textarea from '../../Textarea.svelte';
  import EmployeeDonationService, { type CharityInvitationRequest } from '../../../lib/api/employee/donation.service';
  
  export let isOpen = false;

  const dispatch = createEventDispatcher();

  let charityName = '';
  let website = '';
  let reason_for_request = '';
  let contact_email = '';
  let contact_phone = '';
  let loading = false;
  let error: string | null = null;

  function handleSubmit() {
    if (!charityName.trim() || !contact_email.trim() || !reason_for_request.trim()) {
      error = 'Please fill all required fields';
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact_email.trim())) {
      error = 'Please enter a valid email address';
      return;
    }

    submitInvitation();
  }

  async function submitInvitation() {
    loading = true;
    error = null;

    try {
      // Normalize website URL - add https:// if not present
      let normalizedWebsite = website.trim();
      if (normalizedWebsite && !normalizedWebsite.startsWith('http://') && !normalizedWebsite.startsWith('https://')) {
        normalizedWebsite = `https://${normalizedWebsite}`;
      }
      
      const inviteData: CharityInvitationRequest = {
        name: charityName.trim(),
        contact_email: contact_email.trim(),
        reason_for_request: reason_for_request.trim(),
        website: normalizedWebsite || undefined,
        contact_phone: contact_phone.trim() || undefined
      };

      const result = await EmployeeDonationService.inviteCharity(inviteData);
      
      if (result.success) {
        dispatch('complete');
        resetForm();
      } else {
        error = result.message || 'Failed to send invitation';
      }
    } catch (err: any) {
      error = err.message || 'An unexpected error occurred';
    } finally {
      loading = false;
    }
  }

  function handleCancel() {
    dispatch('cancel');
    resetForm();
  }

  function resetForm() {
    charityName = '';
    website = '';
    reason_for_request = '';
    contact_email = '';
    contact_phone = '';
    error = null;
  }

  function handleBackdropClick(event: MouseEvent) {
    if (event.target === event.currentTarget) {
      handleCancel();
    }
  }
</script>

{#if isOpen}
  <div class="modal-backdrop" role="presentation" on:click|self={handleCancel} on:keydown={(e) => e.key === 'Escape' && handleCancel()}>
    <div class="modal-container" role="dialog" aria-modal="true" aria-labelledby="invite-charity-title">
      <div class="modal-header">
        <h2 id="invite-charity-title">Invite a Charity</h2>
        <button class="close-button" on:click={handleCancel} aria-label="Close">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>

      <div class="modal-body">
        <p class="modal-description">Request a new charity to be added to the platform. The charity will receive an email invitation to complete their registration.</p>

        <div class="info-note" role="note">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="16" x2="12" y2="12"></line>
            <line x1="12" y1="8" x2="12.01" y2="8"></line>
          </svg>
          <span>Your name and email will be included in the invitation so the charity knows who referred them.</span>
        </div>

        {#if error}
          <div class="error-message" role="alert">
            {error}
          </div>
        {/if}

        <div class="form-group">
          <Input
            label="Charity Name *"
            bind:value={charityName}
            placeholder="Enter charity name"
            required={true}
            disabled={loading}
            fullWidth={true}
          />
        </div>

        <div class="form-group">
          <Input
            label="Contact Email *"
            bind:value={contact_email}
            placeholder="Enter contact email"
            type="email"
            required={true}
            disabled={loading}
            fullWidth={true}
          />
        </div>

        <div class="form-group">
          <Input
            label="Contact Phone"
            bind:value={contact_phone}
            placeholder="Enter contact phone"
            type="tel"
            disabled={loading}
            fullWidth={true}
          />
        </div>

        <div class="form-group">
          <Input
            label="Website"
            bind:value={website}
            placeholder="www.charity.org or https://www.charity.org"
            type="text"
            disabled={loading}
            fullWidth={true}
          />
        </div>

        <div class="form-group">
          <Textarea
            label="Reason for Request *"
            bind:value={reason_for_request}
            placeholder="Why would you like this charity to be added?"
            required={true}
            disabled={loading}
            fullWidth={true}
            rows={4}
          />
        </div>
      </div>

      <div class="modal-footer">
        <Button variant="secondary" on:click={handleCancel} disabled={loading}>
          Cancel
        </Button>
        <Button variant="primary" on:click={handleSubmit} disabled={loading}>
          {loading ? 'Sending...' : 'Send Invitation'}
        </Button>
      </div>
    </div>
  </div>
{/if}

<style>
  .modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    backdrop-filter: blur(4px);
  }

  .modal-container {
    background: white;
    border-radius: 12px;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
    max-width: 600px;
    width: 90%;
    max-height: 90vh;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
  }

  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.5rem;
    border-bottom: 1px solid #e5e7eb;
  }

  .modal-header h2 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #111827;
    margin: 0;
  }

  .close-button {
    background: none;
    border: none;
    padding: 0.5rem;
    cursor: pointer;
    color: #6b7280;
    transition: color 0.2s;
    border-radius: 4px;
  }

  .close-button:hover {
    color: #111827;
    background: #f3f4f6;
  }

  .modal-body {
    padding: 1.5rem;
    flex: 1;
    overflow-y: auto;
  }

  .modal-description {
    color: #6b7280;
    font-size: 0.875rem;
    margin-bottom: 1rem;
  }

  .info-note {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    padding: 0.75rem;
    background-color: #eff6ff;
    border: 1px solid #bfdbfe;
    border-radius: 8px;
    margin-bottom: 1.5rem;
    font-size: 0.8125rem;
    color: #1e40af;
    line-height: 1.4;
  }

  .info-note svg {
    flex-shrink: 0;
    margin-top: 1px;
    color: #3b82f6;
  }

  .form-group {
    margin-bottom: 1.25rem;
  }

  .error-message {
    background: #fee2e2;
    color: #991b1b;
    padding: 0.75rem;
    border-radius: 6px;
    margin-bottom: 1rem;
    font-size: 0.875rem;
  }

  .modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
    padding: 1.5rem;
    border-top: 1px solid #e5e7eb;
  }

  @media (max-width: 640px) {
    .modal-container {
      width: 95%;
      max-height: 95vh;
    }

    .modal-header,
    .modal-body,
    .modal-footer {
      padding: 1rem;
    }
  }
</style>

